import {
  WSButton,
  WSButtons,
  WSFormOld,
  WSGrid,
  WSInputDateOld,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  Currency as CurrencyV3,
  RecurrenceScheduleDateStatus,
  RecurringDocumentStatus,
  RecurringInvoice,
  RecurringInvoiceSplit,
  RecurringInvoiceSplitSplitType
} from "@wingspanhq/wingspan-api-sdk";
import { addDays } from "date-fns";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import { WSQueries } from "../../query/WSQuery";
import { useWSMutation } from "../../query/helpers";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { recurringInvoicesApi } from "@api";
import { queriesRecurringInvoices } from "@query";
import { DrawerLayout } from "../components/DrawerLayout/DrawerLayout";

import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { useDefaultCurrency } from "../../shared/hooks/useDefaultCurrency";
import {
  InvoiceSplitsSection,
  InvoiceSplitsSectionValues
} from "../components/InvoicesForm/InvoiceSplitsSection";
import {
  LineItemsSection,
  LineItemsSectionValues
} from "../components/InvoicesForm/LineItemsSection";
import { getValidationSchemaLineItem } from "../components/InvoicesForm/LineItemsSection/validationSchema";
import {
  OtherSection,
  OtherSectionValues
} from "../components/InvoicesForm/OtherSection";
import { selectorRecurringLineItems } from "../selectors/selectorRecurringLineItems";
import { selectorScheduleWithOccurrence } from "../selectors/selectorScheduleWithOccurrence";
import {
  attachmentsFormDataToCreateRequest,
  convertToLineItemFormDataFromRecurring,
  recurringSplitsToFormData
} from "../utils/invoicesFormUtils";
import {
  calculateCalendarDayDifference,
  convertLocalDateToUTCDay,
  getLocalStartDayByUTC
} from "../../utils/dates";

export type SeriesInvoiceFormValues = LineItemsSectionValues &
  InvoiceSplitsSectionValues &
  OtherSectionValues & {
    dueDate: Date;
    sendDate: Date;
  };

const getDefaultValues = (
  baseRecurringInvoice: RecurringInvoice,
  sendDate: Date
) => ({
  lineItems: (baseRecurringInvoice.invoiceTemplate?.lineItems ?? []).map(
    convertToLineItemFormDataFromRecurring
  ),
  splits: recurringSplitsToFormData(
    baseRecurringInvoice.invoiceTemplate?.splits
  ),
  other: {
    projectName: String(baseRecurringInvoice.metadata?.projectName || ""),
    notes: baseRecurringInvoice.invoiceTemplate?.notes || "",
    attachments: (
      baseRecurringInvoice.invoiceTemplate?.customAttachmentIds || []
    ).map(id => ({ fileId: id }))
  },
  purchaseOrderNumber: baseRecurringInvoice.metadata?.purchaseOrderNumber || "",
  dueDate: addDays(
    sendDate,
    baseRecurringInvoice.invoiceTemplate?.paymentTermsDays ?? 0
  ),
  sendDate: sendDate
});

export const InvoicesSeriesInvoiceEdit: React.FC<
  RouteComponentProps<
    {
      invoiceTemplateId: string;
      scheduleDateIndex: string;
    },
    {},
    { backPath?: string }
  >
> = ({ match, location }) => {
  const history = useHistory();

  const parentInvoiceTemplateId = match.params.invoiceTemplateId;
  const scheduleDateIndex = Number(match.params.scheduleDateIndex);

  const queryParentRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId: parentInvoiceTemplateId
    });
  const parentScheduleDate =
    queryParentRecurringInvoice.data?.schedule?.scheduleDates?.[
      scheduleDateIndex
    ];
  const overrideId = parentScheduleDate?.invoiceTemplateId;
  const queryOverrideRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery(
      { recurringInvoiceId: overrideId! },
      { enabled: !!overrideId, retry: false }
    );
  const queryOverrideForGate = !overrideId
    ? { ...queryOverrideRecurringInvoice, isSuccess: true, data: undefined }
    : queryOverrideRecurringInvoice;
  const queryFeatureFlags = useQueryFeatureFlags();
  const defaultCurrency = useDefaultCurrency();

  const isCurrencyConversionActive =
    queryFeatureFlags.data?.currencyConversionToUSD &&
    queryParentRecurringInvoice.data?.currency === Currency.USD;

  const onBack = () => {
    if (location.state?.backPath) {
      history.replace({
        pathname: location.state.backPath,
        search: location.search
      });
    } else {
      history.goBack();
    }
  };

  const parentRecurringInvoice = queryParentRecurringInvoice.data;
  const scheduleDate = parentScheduleDate;

  const [submit, submitMeta] = useWSMutation(
    async (values: SeriesInvoiceFormValues) => {
      if (!parentRecurringInvoice || !scheduleDate) {
        throw new Error("No parent recurring invoice");
      }

      const splits: RecurringInvoiceSplit[] = (values.splits || []).flatMap(
        split => {
          if (
            split.remove ||
            !split.payee?.payeeId ||
            !split.payee?.payeeEngagementId ||
            !split.amount
          ) {
            return [];
          }

          return [
            {
              payeeId: split.payee.payeeId,
              payeeEngagementId: split.payee.payeeEngagementId,
              splitType: RecurringInvoiceSplitSplitType.PAYEE_EARNINGS,
              amount: split.amount,
              description: split.description
            }
          ];
        }
      );

      const sendDate = convertLocalDateToUTCDay(values.sendDate);

      const invoiceTemplateBody = {
        paymentTermsDays: calculateCalendarDayDifference(
          values.dueDate,
          values.sendDate
        ),
        lineItems: selectorRecurringLineItems(values.lineItems),
        splits,
        customAttachmentIds: attachmentsFormDataToCreateRequest(
          values.other.attachments
        ),
        notes: values.other.notes
      };

      const overrideMetadata = {
        parentInvoiceTemplateId,
        ...(values.other.projectName
          ? { projectName: values.other.projectName }
          : {}),
        ...(parentRecurringInvoice.metadata?.purchaseOrderNumber
          ? {
              purchaseOrderNumber:
                parentRecurringInvoice.metadata.purchaseOrderNumber
            }
          : {})
      };

      if (scheduleDate.invoiceTemplateId) {
        const override = queryOverrideRecurringInvoice.data;

        if (!override) {
          throw new Error("Could not load the invoice being edited");
        }

        await recurringInvoicesApi.updateRecurringInvoice({
          recurringInvoiceId: scheduleDate.invoiceTemplateId,
          recurringInvoiceUpdate: {
            schedule: selectorScheduleWithOccurrence({
              schedule: override.schedule,
              index: 0,
              occurrence: { date: sendDate }
            }),
            invoiceTemplate: invoiceTemplateBody,
            metadata: overrideMetadata
          }
        });

        await recurringInvoicesApi.updateRecurringInvoice({
          recurringInvoiceId: parentInvoiceTemplateId,
          recurringInvoiceUpdate: {
            schedule: selectorScheduleWithOccurrence({
              schedule: parentRecurringInvoice.schedule,
              index: scheduleDateIndex,
              occurrence: { date: sendDate }
            })
          }
        });

        onBack();
      } else {
        const created = await recurringInvoicesApi.createRecurringInvoice({
          recurringInvoiceCreate: {
            payerId: parentRecurringInvoice.payerId!,
            payerEngagementId:
              parentRecurringInvoice.payerEngagementId ?? undefined,
            currency: parentRecurringInvoice.currency!,
            schedule: {
              scheduleDates: [
                {
                  date: sendDate,
                  status: RecurrenceScheduleDateStatus.PENDING
                }
              ]
            },
            invoiceTemplate: {
              ...parentRecurringInvoice.invoiceTemplate,
              ...invoiceTemplateBody
            },
            metadata: overrideMetadata,
            status: RecurringDocumentStatus.DRAFT
          }
        });

        try {
          await recurringInvoicesApi.updateRecurringInvoice({
            recurringInvoiceId: parentInvoiceTemplateId,
            recurringInvoiceUpdate: {
              schedule: selectorScheduleWithOccurrence({
                schedule: parentRecurringInvoice.schedule,
                index: scheduleDateIndex,
                occurrence: {
                  status: RecurrenceScheduleDateStatus.MODIFIED,
                  invoiceTemplateId: created.id,
                  date: sendDate
                }
              })
            }
          });
        } catch (error) {
          try {
            await recurringInvoicesApi.cancelRecurringInvoice({
              recurringInvoiceId: created.id!
            });
          } catch {}

          throw error;
        }

        await recurringInvoicesApi.updateRecurringInvoice({
          recurringInvoiceId: created.id!,
          recurringInvoiceUpdate: { status: RecurringDocumentStatus.ACTIVE }
        });

        onBack();
      }
    },
    {
      dependencies: [
        queriesRecurringInvoices.getRecurringInvoice.queryKeyBase,
        queriesRecurringInvoices.listRecurringInvoices.queryKeyBase
      ]
    }
  );

  return (
    <DrawerLayout
      title={`Edit invoice – ${
        scheduleDate
          ? toWSDateString(
              getLocalStartDayByUTC(scheduleDate.date),
              "monthDate"
            )
          : ""
      }`}
      onBack={onBack}
    >
      <WSQueries
        queries={{
          queryParentRecurringInvoice,
          queryOverrideRecurringInvoice: queryOverrideForGate
        }}
      >
        {({
          queryParentRecurringInvoice: { data: parent },
          queryOverrideRecurringInvoice: { data: override }
        }) => {
          const currency = (override?.currency ||
            parent.currency ||
            defaultCurrency) as CurrencyV3 as unknown as Currency;

          return (
            <WSFormOld<SeriesInvoiceFormValues>
              defaultValues={getDefaultValues(
                override ?? parent,
                getLocalStartDayByUTC(
                  parent.schedule?.scheduleDates?.[scheduleDateIndex]
                    ?.date as Date
                )
              )}
              validationSchema={Yup.object().shape({
                lineItems: Yup.array(getValidationSchemaLineItem())
                  .min(1, "At least one work item is required")
                  .required("At least one work item is required"),
                sendDate: Yup.date().isTodayAndInFuture(),
                dueDate: Yup.date().isAfter("sendDate")
              })}
              onSubmit={values => {
                submit(values);
              }}
            >
              <WSText.Heading5 mb="M">Send</WSText.Heading5>
              <WSGrid gutter="M">
                <WSGrid.Item span={{ s: "6" }}>
                  <WSFormOld.Field
                    name="sendDate"
                    component={WSInputDateOld}
                    mb="3XL"
                  />
                </WSGrid.Item>
              </WSGrid>
              <WSText.Heading5 mb="M">Due</WSText.Heading5>
              <WSGrid gutter="M">
                <WSGrid.Item span={{ s: "6" }}>
                  <WSFormOld.Field
                    name="dueDate"
                    component={WSInputDateOld}
                    mb="3XL"
                  />
                </WSGrid.Item>
              </WSGrid>
              <WSText.Heading5 mb="M">Work summary</WSText.Heading5>
              <LineItemsSection
                mb="3XL"
                isCurrencyConversionActive={isCurrencyConversionActive}
                forceMobileShow={true}
                invoiceCurrency={currency}
              />
              <InvoiceSplitsSection mb="3XL" invoiceCurrency={currency} />
              <OtherSection mb="3XL" fullWidth />

              <ErrorAlert
                mb="XL"
                context="UpdateSeriesInvoice"
                error={submitMeta.error}
              />

              <WSButtons format="modal">
                <WSButton type="submit" loading={submitMeta.isLoading}>
                  Update invoice
                </WSButton>
              </WSButtons>
            </WSFormOld>
          );
        }}
      </WSQueries>
    </DrawerLayout>
  );
};
